import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function BriefingTab({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconSummary, setReconSummary] = useState(null);
  const [reconFindings, setReconFindings] = useState([]);
  const [reconRunning, setReconRunning] = useState(false);

  const loadRecon = useCallback(async () => {
    try {
      const [sR, fR] = await Promise.all([
        api.get('/rcm/reconciliation/summary').catch(() => ({ data: null })),
        api.get('/rcm/reconciliation/findings?acknowledged=false&limit=10').catch(() => ({ data: { findings: [] } })),
      ]);
      setReconSummary(sR.data);
      setReconFindings(fR.data.findings || []);
    } catch {}
  }, []);

  const runRecon = async () => {
    setReconRunning(true);
    try {
      await api.post('/rcm/reconciliation/run');
      await loadRecon();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setReconRunning(false); }
  };

  const ackFinding = async (findingId) => {
    try {
      await api.post(`/rcm/reconciliation/findings/${findingId}/acknowledge`, { notes: '' });
      loadRecon();
    } catch {}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [overviewR, followUpR, underpayR, denialR, postingR, priorityR, agingRiskR, _snapR] = await Promise.all([
          api.get('/rcm-analytics/overview'),
          api.get('/rcm/follow-up/queue'),
          api.get('/rcm-analytics/underpayments').catch(() => ({ data: { summary: {}, candidates: [] } })),
          api.get('/rcm/denials/queue?limit=5'),
          api.get('/rcm/remittance/posting/queue?limit=5'),
          api.get('/rcm/predictive/priority?limit=5').catch(() => ({ data: { ranked_items: [] } })),
          api.get('/rcm/predictive/aging-risks?limit=5').catch(() => ({ data: { alerts: [], summary: {} } })),
          api.get('/rcm/predictive/daily-snapshot').catch(() => ({ data: {} })),
        ]);

        // Use Priority Engine V2 for top items (replaces manual scoring)
        const priorityItems = (priorityR.data.ranked_items || []).map(item => ({
          priority: item.priority_score,
          type: item.item_type || item.source_type || 'unknown',
          typeLabel: _typeLabel(item.item_type || item.source_type || ''),
          source: item.source,
          payer: item.payer_name || item.payer_id || '—',
          amount: item.dollar_amount || 0,
          age: item.age_days || 0,
          reason: item.why_this_rank || item.reason || '',
          tab: (item.item_type || '').includes('denial') ? 'denials' : (item.item_type || '').includes('reject') ? 'rejections' : (item.item_type || '').includes('posting') ? 'posting' : 'followup',
          score_breakdown: item.score_breakdown,
          hard_override: item.hard_override,
        }));

        // Predictive alerts (timely filing, aging)
        const criticalAlerts = (agingRiskR.data.alerts || []).filter(a => a.urgency_level === 'critical' || a.urgency_level === 'high').slice(0, 3);

        const overview = overviewR.data;
        let ar60 = 0;
        try {
          const agingR = await api.get('/rcm-analytics/ar-aging');
          const buckets = agingR.data?.buckets || [];
          ar60 = buckets.filter(b => ['61-90', '91-120', '121+'].includes(b.label)).reduce((s, b) => s + b.amount, 0);
        } catch {}

        setData({
          kpis: {
            total_ar: overview?.totals?.total_ar || 0,
            ar_60: ar60,
            denial_dollars: (denialR.data.queue || []).reduce((s, d) => s + (d.denial_amount || 0), 0),
            underpay_dollars: underpayR.data?.summary?.total_underpayment_dollars || 0,
          },
          topItems: priorityItems.slice(0, 5),
          predictiveAlerts: criticalAlerts,
          focusSummary: _buildFocusSummary(overview, followUpR.data, denialR.data, underpayR.data),
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
    loadRecon();
  }, [loadRecon]);

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading briefing...</div>;
  if (!data) return <div className="p-4 text-sm text-red-500">Failed to load briefing</div>;

  return (
    <div className="space-y-5" data-testid="briefing-tab">
      {/* KPI strip — 4 CLICKABLE cards that route to relevant views */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="briefing-kpis">
        <Card className="cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all" onClick={() => onNavigate('remittance', {}, 'Total A/R')} data-testid="kpi-total-ar"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total A/R</p>
          <p className="text-xl font-bold text-amber-600">{_fmtK(data.kpis.total_ar)}</p>
        </CardContent></Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-red-300 transition-all" onClick={() => onNavigate('followup', { age_filter: '60+' }, 'A/R > 60 Days')} data-testid="kpi-ar60"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">A/R &gt; 60 Days</p>
          <p className="text-xl font-bold text-red-600">{_fmtK(data.kpis.ar_60)}</p>
        </CardContent></Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-red-300 transition-all" onClick={() => onNavigate('denials', { status: 'open' }, 'Denial $ Open')} data-testid="kpi-denial"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Denial $ Open</p>
          <p className="text-xl font-bold text-red-600">{fmt(data.kpis.denial_dollars)}</p>
        </CardContent></Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all" onClick={() => onNavigate('posting', { type: 'underpayment' }, 'Underpayments')} data-testid="kpi-underpay"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Underpayments</p>
          <p className="text-xl font-bold text-amber-600">{fmt(data.kpis.underpay_dollars)}</p>
        </CardContent></Card>
      </div>

      {/* Where to Focus — CLICKABLE command cards */}
      <Card className="border-primary/20" data-testid="focus-summary">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-xs uppercase tracking-wider text-primary">Where to Focus Today</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="space-y-1.5">
            {data.focusSummary.map((line, i) => {
              const route = line.route || 'followup';
              const filterObj = line.filters || {};
              return (
                <div key={i} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors"
                     onClick={() => onNavigate(route, filterObj, `Focus: ${line.text?.slice(0, 30) || ''}`)} data-testid={`focus-item-${i}`}>
                  <span className="text-primary font-bold mt-0.5">{i + 1}.</span>
                  <span className="flex-1">{line.text || line}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Predictive Alerts — CLICKABLE, route to follow-up */}
      {data.predictiveAlerts?.length > 0 && (
        <Card className="border-violet-200 bg-violet-50/20" data-testid="predictive-alerts">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-xs flex items-center gap-2"><PredictiveLabel /> Filing & Aging Alerts</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-2">
            {data.predictiveAlerts.map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-xs py-1.5 border-b last:border-0 cursor-pointer hover:bg-violet-50/50 rounded transition-colors"
                   onClick={() => onNavigate('followup', { risk_type: a.risk_type, urgency: a.urgency_level }, `Alert: ${a.risk_type}`)} data-testid={`alert-item-${i}`}>
                <RiskBadge level={a.urgency_level === 'critical' ? 'high' : a.urgency_level} label={a.urgency_level} />
                <span className="w-28 truncate shrink-0">{a.payer_name}</span>
                <span className="flex-1 text-muted-foreground">
                  {a.risk_type === 'timely_filing'
                    ? `${a.days_remaining <= 0 ? `EXPIRED ${Math.abs(a.days_remaining)}d ago` : `${a.days_remaining}d remaining`} (${a.filing_limit_days}d limit)`
                    : `${a.days_in_state}d in ${a.claim_status}`}
                </span>
                <span className="font-medium shrink-0">{fmt(a.total_charge)}</span>
                <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top 5 Priority Items (now powered by Priority Engine V2) */}
      <Card data-testid="priority-items">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-xs flex items-center gap-2">Top Priority Items <PredictiveLabel /></CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="space-y-2">
            {data.topItems.map((item, i) => {
              const typeColors = {
                not_submitted: 'bg-amber-100 text-amber-700', denied_open: 'bg-red-100 text-red-700',
                rejected_open: 'bg-amber-100 text-amber-700', posting_exception: 'bg-red-100 text-red-700',
                remittance_unmatched: 'bg-purple-100 text-purple-700', rebill_ready: 'bg-sky-100 text-sky-700',
                denial_action_needed: 'bg-red-100 text-red-700', underpayment_candidate: 'bg-amber-100 text-amber-700',
                accepted_not_paid: 'bg-orange-100 text-orange-700', submitted_no_response: 'bg-orange-100 text-orange-700',
                denial_open: 'bg-red-100 text-red-700',
              };
              const tc = typeColors[item.type] || 'bg-muted text-muted-foreground';
              return (
                <div key={i} className="cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1"
                     onClick={() => onNavigate(item.tab, { source_type: item.type, payer: item.payer }, `Priority #${i+1}: ${item.typeLabel}`)} data-testid={`priority-item-${i}`}>
                  <div className="flex items-center gap-3 py-2">
                    <span className="text-lg font-bold text-muted-foreground w-6 text-right">{i + 1}</span>
                    {item.hard_override && <span className="text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1">CRITICAL</span>}
                    <span className={`${tc} px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap shrink-0`}>{item.typeLabel}</span>
                    <span className="text-xs w-28 truncate shrink-0">{item.payer}</span>
                    {item.amount > 0 && <span className="text-xs font-medium w-20 text-right shrink-0">{fmt(item.amount)}</span>}
                    {item.age > 0 && <Badge variant={item.age > 90 ? 'destructive' : item.age > 30 ? 'default' : 'secondary'} className="text-[10px] shrink-0">{item.age}d</Badge>}
                    <span className="text-xs text-muted-foreground flex-1 truncate">{item.reason}</span>
                    <span className="text-[10px] text-violet-600 font-medium shrink-0">{item.priority?.toFixed(0)}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </div>
                  {/* Score breakdown — subtle, non-intrusive */}
                  {item.score_breakdown && (
                    <div className="flex gap-2 ml-10 pb-1.5 text-[9px] text-muted-foreground">
                      {Object.entries(item.score_breakdown).filter(([_,v]) => v > 0).map(([k,v]) => (
                        <span key={k} className="bg-muted/50 rounded px-1">{k.replace(/_/g,' ')}:{v.toFixed(0)}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ═══ PHASE 6 Slice 4: Reconciliation Panel ═══ */}
      <Card className="border-orange-200 bg-orange-50/10" data-testid="reconciliation-panel">
        <CardHeader className="pb-1 pt-3 px-4 flex flex-row justify-between items-center">
          <CardTitle className="text-xs flex items-center gap-1.5">
            <ShieldAlert className="h-3 w-3 text-orange-600" />Reconciliation
            {reconSummary?.total_unacknowledged > 0 && (
              <Badge variant="destructive" className="text-[9px] h-4 px-1.5">{reconSummary.total_unacknowledged}</Badge>
            )}
          </CardTitle>
          <div className="flex gap-2 items-center">
            {reconSummary?.latest_run && (
              <span className="text-[10px] text-muted-foreground">Last: {reconSummary.latest_run.started_at?.slice(0,16).replace('T',' ')}</span>
            )}
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={runRecon} disabled={reconRunning} data-testid="run-recon-btn">
              {reconRunning ? <><RotateCw className="h-3 w-3 mr-1 animate-spin" />Running...</> : <><RefreshCw className="h-3 w-3 mr-1" />Run Scan</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {reconSummary && reconSummary.total_unacknowledged > 0 ? (
            <div className="space-y-3">
              {/* Severity breakdown */}
              <div className="flex gap-2">
                {Object.entries(reconSummary.by_severity || {}).map(([sev, count]) => (
                  <div key={sev} className={`text-center rounded p-1.5 flex-1 ${sev === 'critical' ? 'bg-red-100 border border-red-200' : sev === 'high' ? 'bg-orange-100 border border-orange-200' : sev === 'medium' ? 'bg-amber-100 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className="text-lg font-bold">{count}</p>
                    <p className="text-[9px] uppercase text-muted-foreground">{sev}</p>
                  </div>
                ))}
              </div>
              {/* Type breakdown */}
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(reconSummary.by_type || {}).map(([type, count]) => (
                  <span key={type} className="text-[10px] bg-muted rounded px-1.5 py-0.5">{type.replace(/_/g,' ')}: {count}</span>
                ))}
              </div>
              {/* Recent findings */}
              {reconFindings.length > 0 && (
                <div className="space-y-1.5">
                  {reconFindings.slice(0, 5).map((f) => (
                    <div key={f.id} className="flex items-start gap-2 text-xs border-l-2 pl-2 py-1 cursor-pointer hover:bg-muted/20 rounded transition-colors" style={{borderColor: f.severity === 'critical' ? '#dc2626' : f.severity === 'high' ? '#ea580c' : f.severity === 'medium' ? '#d97706' : '#9ca3af'}}
                         onClick={() => onNavigate('operations', { finding_type: f.finding_type }, `Recon: ${f.finding_type}`)} data-testid={`finding-${f.id?.slice(0,8)}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] font-medium uppercase ${f.severity === 'critical' ? 'text-red-700' : f.severity === 'high' ? 'text-orange-600' : 'text-amber-600'}`}>{f.severity}</span>
                          <Badge variant="outline" className="text-[9px] py-0">{f.finding_type?.replace(/_/g,' ')}</Badge>
                        </div>
                        <p className="text-muted-foreground mt-0.5">{f.description}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5 shrink-0" onClick={() => ackFinding(f.id)} data-testid={`ack-${f.id?.slice(0,8)}`}>
                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Ack
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-2">
              {reconSummary ? 'No unacknowledged findings. System is clean.' : 'Run a reconciliation scan to check for discrepancies.'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { BriefingTab };
